import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, Button, Table, Tag, Space, Typography, Alert, Empty } from 'antd';
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  PlusOutlined,
  RiseOutlined,
  SyncOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import { contentPath, modules } from '../../config/const';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val || 0);

const DashboardView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();
  const history = useHistory();

  const [trialBalance, setTrialBalance] = useState<any>(null);
  const [recentEntries, setRecentEntries] = useState<any[]>([]);
  const [fetching, setFetching] = useState(false);

  const loadData = async () => {
    if (!company.current.id) return;
    setFetching(true);
    try {
      const startOfMonth = dayjs().startOf('month').format('YYYY-MM-DD');
      const endOfMonth = dayjs().endOf('month').format('YYYY-MM-DD');

      // Load trial balance for current month
      const tbRes = await securePost('/accounting/reports/trial-balance', {
        company_id: company.current.id,
        start_date: startOfMonth,
        end_date: endOfMonth,
      });
      if (tbRes?.data || tbRes?.report) {
        setTrialBalance(tbRes.data || tbRes.report);
      }

      // Load recent journal entries
      const entriesRes = await securePost('/accounting/journal-entries', {
        company_id: company.current.id,
        limit: 5,
      });
      if (entriesRes?.data || entriesRes?.entries) {
        setRecentEntries(entriesRes.data || entriesRes.entries);
      }
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (company.current.id) {
      loadData();
    }
  }, [company.current.id]);

  const totals = trialBalance?.totals;
  const isBalanced = totals?.is_balanced ?? true;

  const columns = [
    {
      title: 'Comprobante',
      dataIndex: 'entry_number',
      key: 'entry_number',
      render: (num: string, r: any) => (
        <Text strong style={{ color: '#1677ff' }}>
          {num || r.number || (r.id ? `CC-${r.id}` : '—')}
        </Text>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'entry_date',
      key: 'entry_date',
      render: (d: string, r: any) => {
        const val = d || r.date;
        return val ? dayjs(val).format('DD/MM/YYYY') : '—';
      },
    },
    {
      title: 'Concepto',
      dataIndex: 'concept',
      key: 'concept',
      ellipsis: true,
      render: (c: string, r: any) => c || r.description || '—',
    },
    {
      title: 'Total Débito',
      dataIndex: 'total_debit',
      key: 'total_debit',
      align: 'right' as const,
      render: (val: number) => <Text style={{ color: '#1677ff' }}>{formatCurrency(val)}</Text>,
    },
    {
      title: 'Total Crédito',
      dataIndex: 'total_credit',
      key: 'total_credit',
      align: 'right' as const,
      render: (val: number) => <Text style={{ color: '#722ed1' }}>{formatCurrency(val)}</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'POSTED' ? 'green' : status === 'DRAFT' ? 'gold' : 'red'}>
          {status === 'POSTED' ? 'ASENTADO' : status === 'DRAFT' ? 'BORRADOR' : 'ANULADO'}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Panel de Control Contable</Title>
          <Text type="secondary">
            {company.current.name ? `${company.current.name} — Resumen del período contable en curso` : 'Resumen contable'}
          </Text>
        </div>
        <Space>
          <Button icon={<SyncOutlined spin={fetching} />} onClick={loadData} disabled={fetching}>
            Actualizar
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => history.push(contentPath[modules.JournalEntries])}
          >
            Nuevo Comprobante
          </Button>
        </Space>
      </div>

      {/* Balance Indicator Banner */}
      {totals && (
        <Alert
          type={isBalanced ? 'success' : 'error'}
          showIcon
          icon={isBalanced ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span>
                <strong>{isBalanced ? 'Contabilidad Cuadrada' : 'Atención: Descuadre en Libros'}</strong>
                {' — '}
                {isBalanced
                  ? `La suma de Débitos y Créditos coincide exactamente en ${formatCurrency(totals.period_debit)}.`
                  : `Existe una discrepancia de ${formatCurrency(Math.abs(totals.difference))} entre débitos y créditos.`}
              </span>
              <Button size="small" onClick={() => history.push(contentPath[modules.TrialBalance])}>
                Ver Balance Completo
              </Button>
            </div>
          }
        />
      )}

      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: '#f0f5ff', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: '#2f54eb', fontWeight: 600 }}>Débitos del Mes</span>}
              value={totals?.period_debit || 0}
              formatter={(v) => formatCurrency(Number(v))}
              prefix={<WalletOutlined style={{ color: '#2f54eb' }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Cargos acumulados período</Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: '#f9f0ff', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: '#722ed1', fontWeight: 600 }}>Créditos del Mes</span>}
              value={totals?.period_credit || 0}
              formatter={(v) => formatCurrency(Number(v))}
              prefix={<RiseOutlined style={{ color: '#722ed1' }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Abonos acumulados período</Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: '#f6ffed', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: '#52c41a', fontWeight: 600 }}>Cuentas Activas</span>}
              value={trialBalance?.accounts?.length || 0}
              prefix={<ApartmentOutlined style={{ color: '#52c41a' }} />}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Cuentas con movimientos</Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} style={{ background: '#fff7e6', borderRadius: 8 }}>
            <Statistic
              title={<span style={{ color: '#fa8c16', fontWeight: 600 }}>Diferencia de Cuadre</span>}
              value={Math.abs(totals?.difference || 0)}
              formatter={(v) => formatCurrency(Number(v))}
              valueStyle={{ color: totals?.difference ? '#cf1322' : '#3f8600' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>Partida doble ($\Delta$ = 0)</Text>
          </Card>
        </Col>
      </Row>

      {/* Quick Action Tiles */}
      <Card title="Accesos Rápidos al Sistema Contable" bordered={false} style={{ borderRadius: 8 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card
              hoverable
              onClick={() => history.push(contentPath[modules.ChartOfAccounts])}
              style={{ textAlign: 'center', borderColor: '#d9d9d9', borderRadius: 8 }}
            >
              <ApartmentOutlined style={{ fontSize: 28, color: '#1890ff', marginBottom: 8 }} />
              <div style={{ fontWeight: 600 }}>Catálogo PUC</div>
              <Text type="secondary" style={{ fontSize: 12 }}>Árbol de cuentas COLGAAP/NIIF</Text>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              hoverable
              onClick={() => history.push(contentPath[modules.TrialBalance])}
              style={{ textAlign: 'center', borderColor: '#d9d9d9', borderRadius: 8 }}
            >
              <FileTextOutlined style={{ fontSize: 28, color: '#52c41a', marginBottom: 8 }} />
              <div style={{ fontWeight: 600 }}>Balance de Comprobación</div>
              <Text type="secondary" style={{ fontSize: 12 }}>Saldos iniciales y finales</Text>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card
              hoverable
              onClick={() => history.push(contentPath[modules.IncomeStatement])}
              style={{ textAlign: 'center', borderColor: '#d9d9d9', borderRadius: 8 }}
            >
              <RiseOutlined style={{ fontSize: 28, color: '#722ed1', marginBottom: 8 }} />
              <div style={{ fontWeight: 600 }}>Estado de Resultados</div>
              <Text type="secondary" style={{ fontSize: 12 }}>Ingresos, costos y utilidad</Text>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Recent Entries Table */}
      <Card
        title="Últimos Comprobantes de Diario"
        bordered={false}
        style={{ borderRadius: 8 }}
        extra={
          <Button type="link" onClick={() => history.push(contentPath[modules.JournalEntries])}>
            Ver Todos los Comprobantes &rarr;
          </Button>
        }
      >
        <Table
          dataSource={recentEntries}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={fetching}
          size="middle"
          locale={{ emptyText: <Empty description="No se han registrado comprobantes contables aún." /> }}
        />
      </Card>
    </div>
  );
};

export default DashboardView;
